import { useNavigate, useParams } from "@tanstack/react-router";
import { PMAvatar, PMBadge, PMBreadcrumb, PMButton, PMCard, PMEmptyState, PMIcon, PMSkeleton } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useReminders, useVisits } from "@/data/queries";
import { levelDef } from "@/lib/levels";
import { LocationMap } from "@/ui/LocationMap";
import { FieldVisitCard } from "@/ui/FieldVisitCard";
import { Character } from "@/ui/Character";
import { Kpi } from "@/ui/Kpi";
import { avatarColor, relTime, shortDate } from "@/ui/format";
import { recency } from "@/features/locations/LocationsPage";
import { formatBytes, usePaperTrail, useClientRows } from "./queries";
import { FileTile, useOpenFile } from "./files";
import "./clients.css";

// Client profile: who they are, every farm and field they own, the visit
// history across all of it, what's open, and their Paper Trail.
export function ClientPage() {
  const { teamId, clientId } = useParams({ from: "/t/$teamId/clients/$clientId" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const { data, isLoading } = useClientRows();
  const c = data.find((x) => x.id === clientId);
  const scope = c?.scope ?? [];
  const visits = useVisits({ locationIds: scope });
  const tasks = useReminders();
  const trail = usePaperTrail();
  const openFile = useOpenFile();

  if (!c) {
    return isLoading || !data.length
      ? <div className="page page--wide"><PMSkeleton shape="rect" height={180} radius={18} /><PMSkeleton shape="rect" height={420} radius={18} style={{ marginTop: 16 }} /></div>
      : <div className="page"><PMEmptyState title="Client not found" description="This client isn't synced to this device." action={<PMButton onClick={() => nav({ to: "/t/$teamId/clients", params: { teamId } })}>Client portfolio</PMButton>} /></div>;
  }

  const open = tasks.data.filter((t) => t.map_feature_id && scope.includes(t.map_feature_id) && !t.completed_at && !t.canceled_at);
  const files = trail.data.filter((f) => f.locationId && scope.includes(f.locationId));
  const direct = c.locations.filter((l) => l.direct);
  const firstDirect = direct[0];
  const lastByLoc = new Map<string, string>();
  for (const v of visits.data) if (v.map_feature_id && !lastByLoc.has(v.map_feature_id)) lastByLoc.set(v.map_feature_id, v.started_at || "");
  // A location's last visit includes visits to anything beneath it.
  const descendants = (id: string): string[] => c.locations.filter((l) => l.parentId === id).flatMap((l) => [l.id, ...descendants(l.id)]);
  const lastFor = (id: string) => [id, ...descendants(id)].map((k) => lastByLoc.get(k)).filter(Boolean).sort().at(-1) ?? null;

  return (
    <div className="page page--wide client-page">
      <PMBreadcrumb items={[{ label: "Client portfolio", href: `/t/${teamId}/clients` }, { label: c.name || "Client" }]} onNavigate={(it: { href?: string }) => it.href && nav({ to: it.href } as any)} />

      <section className="client-hero card">
        <PMAvatar name={c.name || "?"} size={72} color={avatarColor(c.id)} />
        <div className="client-hero-id">
          <span className="loc-level">Client</span>
          <h1 className="client-hero-name">{c.name || "Unnamed client"}</h1>
          <div className="meta">{c.role || "Client"}<span className="sep" />{direct.length} linked {direct.length === 1 ? "location" : "locations"}<span className="sep" />Client since {shortDate(c.created_at)}</div>
          {c.notes && <p className="muted client-notes">{c.notes}</p>}
        </div>
        <div className="client-hero-actions">
          {c.phone && <PMButton variant="outline" icon="phone" href={`tel:${c.phone.replace(/[^\d+]/g, "")}`}>{c.phone}</PMButton>}
          {c.email && <PMButton icon="chat-dots" href={`mailto:${c.email}`}>Email</PMButton>}
        </div>
      </section>

      <div className="kpis">
        <Kpi emoji="📋" tone="tone-brand" value={visits.data.length} label="Field visits" />
        <Kpi emoji="📆" tone="tone-photo" value={c.visits30} label="Visits, last 30 days" />
        <Kpi emoji="✅" tone="tone-warn" value={open.length} label="Open tasks" />
        <Kpi emoji="🗂️" tone="tone-note" value={files.length} label="Files in Paper Trail" />
      </div>

      <div className="detail">
        <div className="client-main">
          <PMCard eyebrow={`Locations · ${c.locations.length}`} icon="map-pin-outline">
            <div className="client-map">
              <LocationMap height={320} onSelect={(id) => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: id } })}
                items={c.locations.map((l) => { const rc = recency(lastFor(l.id)); return { id: l.id, label: l.label || "", geometry: l.geometry, latitude: l.latitude, longitude: l.longitude, tone: rc.tone, sub: lastFor(l.id) ? `Visited ${rc.label}` : "Never visited" }; })} />
            </div>
            <ul className="client-loclist">
              {c.locations.map((l) => {
                const d = levelDef(l.level, hierarchy.key);
                const last = lastFor(l.id);
                const rc = recency(last);
                return (
                  <li key={l.id}>
                    <button className={`list-row ${l.direct ? "" : "is-child"}`} onClick={() => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: l.id } })}>
                      <span className="emoji-disc">{d.emoji}</span>
                      <span className="list-main"><span className="list-title">{l.label}</span><span className="meta">{d.label}{l.description && <><span className="sep" />{l.description}</>}</span></span>
                      <PMBadge variant={rc.badge} dot>{last ? `Visited ${rc.label}` : "Never visited"}</PMBadge>
                      <span className="chev"><PMIcon name="chevron-right" size={18} /></span>
                    </button>
                  </li>
                );
              })}
              {!c.locations.length && <li className="hint client-empty">No locations are linked to this client yet.</li>}
            </ul>
          </PMCard>

          <section>
            <div className="eyebrow"><span>Recent visits</span><span className="count-pill">{visits.data.length}</span></div>
            {visits.data.length ? (
              <div className="visit-grid">{visits.data.slice(0, 6).map((v) => <FieldVisitCard key={v.id} v={v} onOpen={() => nav({ to: "/t/$teamId/visits/$visitId", params: { teamId, visitId: v.id } })} />)}</div>
            ) : <Character name="bukkie" size={110} title="No visits yet" line="Visits to this client's locations appear here after they sync." />}
          </section>
        </div>

        <aside className="detail-rail">
          <PMCard eyebrow="Open tasks" icon="check-circle-outline" count={open.length} action={{ label: "All tasks", icon: "chevron-right", onClick: () => nav({ to: "/t/$teamId/tasks", params: { teamId } }) }}>
            {open.length ? (
              <ul className="mini-list">
                {open.slice(0, 6).map((t) => (
                  <li key={t.id}><span className="mini-dot" /><span className="list-main"><span className="mini-title">{t.details}</span><span className="meta">{t.location_label}<span className="sep" />{t.reminder_date ? relTime(t.reminder_date) : "No date"}</span></span></li>
                ))}
              </ul>
            ) : <p className="hint">Nothing open for this client.</p>}
          </PMCard>

          <PMCard eyebrow="Paper Trail" icon="document-outline" count={files.length}
            action={firstDirect ? { label: "Open", icon: "chevron-right", onClick: () => nav({ to: "/t/$teamId/clients/paper-trail", params: { teamId }, search: { location: firstDirect.id } }) } : undefined}>
            {files.length ? (
              <ul className="mini-files">
                {files.slice(0, 6).map((f) => (
                  <li key={f.id}>
                    <button className="mini-file" onClick={() => openFile(f)} title={`Open ${f.name}`}>
                      <FileTile kind={f.type} size={30} />
                      <span className="list-main"><span className="mini-title">{f.name}</span><span className="meta">{f.locationLabel}<span className="sep" />{f.reportId ? `Signed ${shortDate(f.date)}` : formatBytes(f.size)}</span></span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : <p className="hint">No files linked to this client's locations.</p>}
          </PMCard>
        </aside>
      </div>
    </div>
  );
}
